import React from 'react';
import Button from '../../../Button';
import apiService from '../../../../services/api';

/**
 * Step 5: Completion Summary & Download
 * 
 * Purpose: Confirm success and provide PDF to user
 * 
 * Features:
 * - Success confirmation banner
 * - PDF preview/download info
 * - Download button
 * - Finish button (back to document list)
 */
const Step5_CompletionSummary = ({
  wizardData,
  template,
  onFinish
}) => {
  const downloadPdf = async () => {
    if (!wizardData.pdfUrl) {
      alert('PDF URL not available. Please try again.');
      return;
    }

    try {
      // Extract filename from URL (e.g., "/api/document-requirements/download/document_xyz.pdf" -> "document_xyz.pdf")
      const filename = wizardData.pdfUrl.split('/').pop();
      
      if (!filename) {
        throw new Error('Could not extract filename from PDF URL');
      }

      console.log('[Step5] Downloading PDF:', filename);

      // Use API service to download PDF (includes authentication)
      const pdfBlob = await apiService.downloadDocumentPDF(filename);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template?.title || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      window.URL.revokeObjectURL(url);
      
      console.log('[Step5] PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const openPdfInNewTab = async () => {
    if (!wizardData.pdfUrl) {
      alert('PDF URL not available. Please try again.');
      return;
    }

    try {
      const filename = wizardData.pdfUrl.split('/').pop();
      
      if (!filename) {
        throw new Error('Could not extract filename from PDF URL');
      }

      console.log('[Step5] Opening PDF in new tab:', filename);

      // Fetch PDF with authentication headers
      const pdfBlob = await apiService.downloadDocumentPDF(filename);

      // Create a blob URL and open in new tab
      const blobUrl = window.URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
      
      // Clean up the blob URL after a delay (browser keeps reference to it)
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      console.log('[Step5] PDF opened in new tab');
    } catch (error) {
      console.error('Error opening PDF:', error);
      alert('Failed to open PDF. Please try again.');
    }
  };

  const pdfFileName = template?.title ? `${template.title.replace(/\s+/g, '_')}.pdf` : 'document.pdf';

  return (
    <div className="space-y-6">
      {/* Success Banner */}
      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="text-4xl text-green-600">✓</div>
          <div>
            <h2 className="text-2xl font-bold text-green-900">Document Successfully Completed</h2>
            <p className="text-green-800 mt-2">
              Your document has been generated and securely stored.
            </p>
          </div>
        </div>
      </div>

      {/* Document Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-4 text-gray-900">Completion Summary</h3>
        
        <dl className="space-y-4">
          {template && (
            <div>
              <dt className="text-sm font-semibold text-gray-600">Document</dt>
              <dd className="text-gray-900">{template.title}</dd>
            </div>
          )}
          
          {wizardData.clubName && (
            <div>
              <dt className="text-sm font-semibold text-gray-600">Club</dt>
              <dd className="text-gray-900">{wizardData.clubName}</dd>
            </div>
          )}
          
          {wizardData.documentId && (
            <div>
              <dt className="text-sm font-semibold text-gray-600">Document ID</dt>
              <dd className="font-mono text-sm text-gray-900 break-all">{wizardData.documentId}</dd>
            </div>
          )}
          
          {wizardData.completed_at && (
            <div>
              <dt className="text-sm font-semibold text-gray-600">Completed</dt>
              <dd className="text-gray-900">
                {new Date(wizardData.completed_at).toLocaleString()}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* PDF Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-3">Your Document</h4>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-800">
              <strong>File:</strong> {pdfFileName}
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Your PDF is ready to download
            </p>
          </div>
          <svg className="w-10 h-10 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.5 3.5a.5.5 0 11-1 0 .5.5 0 011 0zM9 8a1 1 0 100-2 1 1 0 000 2zm-4 6a1 1 0 100-2 1 1 0 000 2zm3-2a1 1 0 11-2 0 1 1 0 012 0z"/>
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
          </svg>
        </div>
      </div>

      {/* Download Section */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={downloadPdf}
          variant="primary"
          disabled={!wizardData.pdfUrl}
          className="flex-1 sm:flex-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PDF
        </Button>
        
        <Button
          onClick={openPdfInNewTab}
          variant="outline"
          disabled={!wizardData.pdfUrl}
          className="flex-1 sm:flex-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          Open in New Tab
        </Button>
      </div>

      {/* What's Next */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3">What Happens Next</h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>✓ Your document has been saved to your account</li>
          <li>✓ You can access it anytime from your Documents page</li>
          <li>✓ A confirmation email may be sent to your registered email</li>
          <li>✓ Share the document with your club administrators if needed</li>
        </ul>
      </div>

      {/* Next Steps / Additional Actions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Questions?</strong> Contact your club administrator for assistance or more information about your document.
        </p>
      </div>
    </div>
  );
};

export { Step5_CompletionSummary };
